// src/components/StockScreener/StockScreener.jsx
//
// Drop the entire StockScreener/ folder into src/components/
// Import wherever you need it:
//   import StockScreener from '../components/StockScreener/StockScreener';
//
// Uses:
//   React Router  — no <Link> used, just internal state
//   Your existing auth — screener works in demo mode if not logged into Upstox
//   CSS Modules   — scoped, won't conflict with your existing styles

import { useState } from 'react';
import styles from './StockScreener.module.css';
import { useUpstox } from './useUpstox';
import { SCREENER_DATA, TF_LABELS } from './screenerData';
import { NIFTY50_KEYS,HistoricalDataStoring } from '../../../services/auth-service/upstoxService';
import axios from 'axios';
import {
  fmt, calcRR, scoreColor, sigLabel,
  indicatorClass, rsiLabel, adxLabel,
} from './screenerUtils';

// ── Merge live LTP into mock rows by index position
function withLivePrices(rows, livePrices) {
  if (!livePrices || !Object.keys(livePrices).length) return rows;
    console.log("LIVE PRICES:", livePrices);
  return rows.map((row, i) => {
    const ltp = livePrices[NIFTY50_KEYS[i]];
    return ltp ? { ...row, ltp } : row;
  });
}

// ────────────────────────────────────────────
//  BADGE
// ────────────────────────────────────────────
function Badge({ sig }) {
  const cls = {
    'strong-buy': styles.badgeStrongBuy,
    buy:          styles.badgeBuy,
    sell:         styles.badgeSell,
    hold:         styles.badgeHold,
  }[sig] ?? '';
  return <span className={`${styles.badge} ${cls}`}>{sigLabel(sig)}</span>;
}

// ────────────────────────────────────────────
//  SCORE BAR
// ────────────────────────────────────────────
function ScoreBar({ score }) {
  const color = scoreColor(score);
  return (
    <div className={styles.scoreBar}>
      <div className={styles.barOuter}>
        <div className={styles.barInner} style={{ width: `${score}%`, background: color }} />
      </div>
      <span className={styles.scoreNum} style={{ color }}>{score}</span>
    </div>
  );
}

// ────────────────────────────────────────────
//  DETAIL PANEL
// ────────────────────────────────────────────
function DetailPanel({ row, isFO, onClose, onToast }) {
  if (!row) return null;

  const rrv       = calcRR(row.entry, row.target, row.sl);
  const profitPct = (((row.target - row.entry) / row.entry) * 100).toFixed(1);
  const lossPct   = (((row.entry  - row.sl)    / row.entry) * 100).toFixed(1);

  const indicators = [
    { label: 'RSI (14)',        val: `${row.rsi} — ${rsiLabel(row.rsi)}`,  cls: indicatorClass(row.rsi,  'rsi')  },
    { label: 'MACD',            val: row.macd,                              cls: indicatorClass(row.macd, 'macd') },
    { label: 'Bollinger Bands', val: row.bb,                                cls: indicatorClass(row.bb,   'bb')   },
    { label: 'EMA Signal',      val: row.ema,                               cls: indicatorClass(row.ema,  'ema')  },
    { label: 'ADX',             val: `${row.adx} — ${adxLabel(row.adx)}`, cls: indicatorClass(row.adx,  'adx')  },
  ];

  function copySetup() {
    const text = [
      `Symbol   : ${row.sym}`,
      `Signal   : ${sigLabel(row.sig)}`,
      `Entry    : ₹${fmt(row.entry)}`,
      `Target   : ₹${fmt(row.target)}`,
      `SL       : ₹${fmt(row.sl)}`,
      `R:R      : ${rrv}:1`,
      `Score    : ${row.score}/100`,
      `Strategy : ${row.strategy}`,
    ].join('\n');
    navigator.clipboard.writeText(text).then(() => onToast('Trade setup copied!'));
  }

  return (
    <div className={styles.detailPanel}>
      <button className={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
      <div className={styles.detailTitle}>{row.sym} — Trade Setup</div>

      <div className={styles.detailGrid}>
        {[
          { label: 'Entry',        val: `₹${fmt(row.entry)}`,                               color: null },
          { label: 'Target',       val: `₹${fmt(row.target)}  (+${profitPct}%)`,            color: '#1D9E75' },
          { label: 'Stop Loss',    val: `₹${fmt(row.sl)}  (−${lossPct}%)`,                  color: '#E24B4A' },
          { label: 'Risk : Reward',val: `${rrv} : 1`,                                        color: null },
          { label: 'Signal Score', val: `${row.score} / 100`,                                color: scoreColor(row.score) },
          { label: 'Volume',       val: row.vol,                                              color: null },
        ].map(m => (
          <div className={styles.detailMetric} key={m.label}>
            <div className={styles.dmLabel}>{m.label}</div>
            <div className={styles.dmVal} style={m.color ? { color: m.color } : {}}>{m.val}</div>
          </div>
        ))}
      </div>

      <div className={styles.indicatorsLabel}>Technical Indicators</div>
      {indicators.map(ind => (
        <div className={styles.indicatorRow} key={ind.label}>
          <span className={styles.indName}>{ind.label}</span>
          <span className={`${styles.indVal} ${styles[ind.cls] ?? ''}`}>{ind.val}</span>
        </div>
      ))}

      {isFO && (
        <div className={styles.foBlock}>
          <span><span className={styles.foLabel}>Strike</span> {row.strike}</span>
          <span><span className={styles.foLabel}>Type</span> {row.type}</span>
          <span><span className={styles.foLabel}>Expiry</span> {row.expiry}</span>
          <span><span className={styles.foLabel}>Lot size</span> {row.lotsize}</span>
          <span><span className={styles.foLabel}>1-lot cost</span> ₹{fmt(row.entry * row.lotsize)}</span>
        </div>
      )}

      <div className={styles.actionBtns}>
        <button className={styles.actionBtn} onClick={copySetup}>📋 Copy Setup</button>
        <button className={styles.actionBtn} onClick={() => onToast('Backtesting module coming next!')}>📊 Backtest</button>
        <button className={styles.actionBtn} onClick={() => onToast(`Alert set for ${row.sym}!`)}>🔔 Set Alert</button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
//  HISTORIC DATA MODAL
// ────────────────────────────────────────────
function HistoricDataModal({ onClose, onStored }) {
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { storeHistorical } = useUpstox();

  const handleStore = async () => {
  if (!month) {
    setError('Please select a month');
    return;
  }

  setLoading(true);
  setError('');
  setMessage('Storing historic data...');

  try {
    const payload = {
      month: month,
      symbols: [
        'NSE_EQ|INE002A01018',
        'NSE_EQ|INE062A01033',
        'NSE_EQ|INE040A01034',
        'NSE_EQ|INE022A01019',
        'NSE_EQ|INE001A01036',
      ]
    };

    const creds = HistoricalDataStoring();

    const res = await axios.post(
      creds[1],   // URL
      payload,    // body
      creds[0]    // headers/config
    );

    const data = res.data;

    if (data.success) {
      setMessage(`✓ Success! Stored ${data.rows_inserted} minute candles for ${month}`);

      setTimeout(() => {
        onStored(data);
        onClose();
      }, 1500);
    } else {
      setError(data.message || 'Failed to store data');
    }

  } catch (err) {
    console.error(err);
    setError(err.response?.data?.message || 'Error storing data');
  } finally {
    setLoading(false);
  }
};

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Store Historic Minute Data</h3>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div className={styles.modalBody}>
          <p className={styles.modalDesc}>
            Select a month to fetch and store minute-wise candle data in ClickHouse
          </p>

          <label className={styles.formLabel}>Month (YYYY-MM)</label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className={styles.input}
            disabled={loading}
          />

          {message && <div className={styles.successMsg}>{message}</div>}
          {error && <div className={styles.errorMsg}>{error}</div>}

          <div className={styles.modalActions}>
            <button
              className={styles.secondaryBtn}
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className={styles.primaryBtn}
              onClick={handleStore}
              disabled={loading || !month}
            >
              {loading ? '⏳ Processing...' : '📥 Store Data'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
//  MAIN COMPONENT
// ────────────────────────────────────────────
export default function StockScreener() {
  const [tf,       setTf]      = useState('intraday');
  const [openSym,  setOpenSym] = useState(null);
  const [universe, setUniverse]= useState('NIFTY 50');
  const [strategy, setStrategy]= useState('All Strategies');
  const [toast,    setToast]   = useState({ msg: '', visible: false });
  const [showHistoric, setShowHistoric] = useState(false);

  const { auth, screenerScan, useLTPPoller } = useUpstox();
  const isFO = tf === 'fo';

  // Live LTP — only polls when authenticated

  const { prices: livePrices } = useLTPPoller(
    auth.authenticated ? NIFTY50_KEYS : [],
    5000
  );

  const baseRows = SCREENER_DATA[tf];
  const rows     = baseRows;
  const openRow  = rows.find(r => r.sym === openSym) ?? null;

  const buys  = rows.filter(r => ['buy', 'strong-buy'].includes(r.sig)).length;
  const sells = rows.filter(r => r.sig === 'sell').length;

  // Toast
  function showToast(msg) {
    setToast({ msg, visible: true });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 2500);
  }

  // CSV export
  function exportCSV() {
    const header = ['Symbol', 'LTP', 'Signal', 'Entry', 'Target', 'SL', 'R:R', 'Score', 'Strategy',
      ...(isFO ? ['Strike', 'Type', 'Expiry', 'LotSize'] : [])];
    const lines = [header.join(',')];
    rows.forEach(r => {
      const row = [r.sym, r.ltp, sigLabel(r.sig), r.entry, r.target, r.sl,
        `${calcRR(r.entry, r.target, r.sl)}:1`, r.score, r.strategy];
      if (isFO) row.push(r.strike, r.type, r.expiry, r.lotsize);
      lines.push(row.join(','));
    });
    const a    = document.createElement('a');
    a.href     = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
    a.download = `signals_${tf}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    showToast('CSV exported!');
  }

  // Scan
  async function runScan() {
    if (!auth.authenticated) { showToast('Login with Upstox to scan live data'); return; }
    try {
      await screenerScan.execute();
      showToast('Scan complete — signals updated');
    } catch {
      showToast('Scan failed — check connection');
    }
  }

  // Tab switch
  function switchTF(newTF) { setTf(newTF); setOpenSym(null); }

  // Row click — toggle detail panel
  function toggleRow(sym) { setOpenSym(prev => prev === sym ? null : sym); }

  return (
    <div className={styles.wrapper}>

      {/* ── Top bar ── */}
      <div className={styles.topbar}>
        <div className={styles.titleBlock}>
          <h2 className={styles.title}>NSE Stock Screener</h2>
          <p className={styles.subtitle}>
            {auth.authenticated
              ? <span className={styles.live}>● Live · Upstox connected</span>
              : <span className={styles.demo}>● Demo mode · not connected</span>}
          </p>
        </div>

        <div className={styles.controls}>
          {auth.authenticated
            ? <button className={styles.linkBtn} onClick={auth.logout}>Disconnect Upstox</button>
            : <button className={styles.linkBtn} onClick={auth.login}>Connect Upstox ↗</button>}

          <select className={styles.select} value={universe} onChange={e => setUniverse(e.target.value)}>
            <option>NIFTY 50</option>
            <option>NIFTY 100</option>
            <option>NIFTY 500</option>
            <option>F&amp;O Stocks</option>
          </select>

          <select className={styles.select} value={strategy} onChange={e => setStrategy(e.target.value)}>
            <option>All Strategies</option>
            <option>Momentum</option>
            <option>Breakout</option>
            <option>Reversal</option>
            <option>Trend Following</option>
          </select>

          <button className={styles.primaryBtn} onClick={runScan} disabled={screenerScan.loading}>
            {screenerScan.loading ? <><span className={styles.spinner} /> Scanning…</> : '▶ Run Scan'}
          </button>

          <button className={styles.secondaryBtn} onClick={() => setShowHistoric(true)}>
            💾 Store Historic Data
          </button>

          <button className={styles.secondaryBtn} onClick={exportCSV}>⬇ CSV</button>
        </div>
      </div>

      {/* ── Metric cards ── */}
      <div className={styles.metrics}>
        {[
          { label: 'Scanned',     val: 50,      sub: universe,        mod: '' },
          { label: 'Buy Signals', val: buys,    sub: 'Strong + Regular', mod: styles.green },
          { label: 'Sell Signals',val: sells,   sub: 'Avoid / Exit',  mod: styles.red },
          { label: 'Market Mood', val: 65,      sub: 'Bullish bias',  mod: styles.amber },
          { label: 'Avg Win Rate',val: '67%',   sub: 'Backtested',    mod: '' },
        ].map(m => (
          <div className={`${styles.metric} ${m.mod}`} key={m.label}>
            <div className={styles.metricLabel}>{m.label}</div>
            <div className={styles.metricVal}>{m.val}</div>
            <div className={styles.metricSub}>{m.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Timeframe tabs ── */}
      <div className={styles.tabs}>
        {Object.keys(TF_LABELS).map(key => (
          <button
            key={key}
            className={`${styles.tab} ${tf === key ? styles.tabActive : ''}`}
            onClick={() => switchTF(key)}
          >
            {key === 'fo' ? 'F&O' : key.charAt(0).toUpperCase() + key.slice(1)}
          </button>
        ))}
      </div>

      {/* ── Detail panel ── */}
      {openRow && (
        <DetailPanel
          row={openRow}
          isFO={isFO}
          onClose={() => setOpenSym(null)}
          onToast={showToast}
        />
      )}

      {/* ── Table ── */}
      <div className={styles.sectionLabel}>{TF_LABELS[tf]}</div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Symbol</th>
              <th>LTP {auth.authenticated && <span className={styles.livePill}>LIVE</span>}</th>
              <th>Signal</th>
              <th>Entry</th>
              <th>Target</th>
              <th>Stop Loss</th>
              <th>R:R</th>
              <th>Score</th>
              <th>Strategy</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const rrv = calcRR(row.entry, row.target, row.sl);
              return (
                <tr
                  key={row.sym + (row.strike ?? '')}
                  className={openSym === row.sym ? styles.rowSelected : ''}
                  onClick={() => toggleRow(row.sym)}
                >
                  <td className={styles.symCell}>
                    {row.sym}
                    {isFO && (
                      <span className={styles.expSmall}>
                        {row.type} {row.strike} · {row.expiry}
                      </span>
                    )}
                  </td>
                  <td>₹{fmt(row.ltp)}</td>
                  <td><Badge sig={row.sig} /></td>
                  <td>₹{fmt(row.entry)}</td>
                  <td className={styles.green}>₹{fmt(row.target)}</td>
                  <td className={styles.red}>₹{fmt(row.sl)}</td>
                  <td className={styles.bold}>{rrv}:1</td>
                  <td><ScoreBar score={row.score} /></td>
                  <td className={styles.muted}>{row.strategy}</td>
                  <td className={styles.chevron}>›</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className={styles.footer}>
        {auth.authenticated
          ? 'Live prices via Upstox · refreshes every 5 seconds'
          : 'Connect Upstox to enable live prices and real-time scanning'}
      </p>

      {/* ── Toast ── */}
      <div className={`${styles.toast} ${toast.visible ? styles.toastShow : ''}`}>
        {toast.msg}
      </div>

      {/* ── Historic Data Modal ── */}
      {showHistoric && (
        <HistoricDataModal
          onClose={() => setShowHistoric(false)}
          onStored={(data) => showToast(`✓ Stored ${data.rows_inserted} rows for ${data.month}`)}
        />
      )}
    </div>
  );
}
